import { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./Modal.module.css";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { Typography } from "ui-kit/Typography";
import classNames from "classnames";
import { useWebTranslation } from "translations";
import { Close } from "../../icons";

export const Modal = ({
  children,
  isOpen,
  onClose,
  title,
  width,
  titleHidden,
  background,
  color,
  stickyHeader,
  maxWidth,
  fillHeight,
}: {
  children: ReactNode;
  isOpen: boolean;
  onClose: VoidFunction;
  title: string;
  width?: "full" | "small";
  /** Keeps `title` for screen readers only, for modals that draw their own header. */
  titleHidden?: boolean;
  /** Overrides the white surface, for modals that layer their own cards on it. */
  background?: string;
  /**
   * The ink for everything inside. A sheet portals out of the page, so it
   * inherits nothing from the screen that opened it; the redesign's Typography
   * carries no colour of its own, where the V1 one forced text/primary.
   */
  color?: string;
  /**
   * Keeps the title and the close button in view while the body scrolls. Opt
   * in: every modal that has not asked for it keeps the header it has today.
   */
  stickyHeader?: boolean;
  /**
   * Caps the shell. It is `width: max-content` otherwise, so a sheet whose
   * content could run wide takes the whole 1024 on a desktop.
   */
  maxWidth?: number;
  /**
   * Lays the sheet out as a column and gives the body the height the header
   * leaves, so a step-by-step sheet can keep its action on the bottom edge —
   * the liability add-on's questions. Opt in, like `stickyHeader`.
   */
  fillHeight?: boolean;
}) => {
  const { t } = useWebTranslation();
  const [localIsOpen, setLocalIsOpen] = useState(isOpen);
  const openedFrom = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Trigger a re-render to animate the modal opening
    setLocalIsOpen(isOpen);
  }, [isOpen]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        {/* The overlay is the scroll container, so the content sits inside it
            rather than beside it. */}
        <Dialog.Overlay
          className={classNames(styles.overlay, localIsOpen && styles.open)}
        >
          <Dialog.Content
            className={classNames(
              styles.content,
              localIsOpen && styles.open,
              width === "full" && styles.full,
              width === "small" && styles.small,
              fillHeight && styles.fillHeight
            )}
            // The title names the dialog; nothing in it describes it.
            aria-describedby={undefined}
            // Radix gives focus back to a Dialog.Trigger, and callers open
            // this from their own buttons, so it would land on <body>. Give it
            // back to whatever had it, as Reach did.
            onOpenAutoFocus={() => {
              openedFrom.current = document.activeElement as HTMLElement | null;
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              openedFrom.current?.focus();
            }}
            style={
              background || color || maxWidth
                ? {
                    ...(background ? { background } : {}),
                    ...(color ? { color } : {}),
                    ...(maxWidth ? { maxWidth } : {}),
                  }
                : undefined
            }
          >
            <div
              className={classNames(
                styles.header,
                stickyHeader && styles.headerSticky
              )}
            >
              {titleHidden ? (
                <div style={{ flex: 1 }}>
                  <Dialog.Title asChild>
                    <VisuallyHidden>{title}</VisuallyHidden>
                  </Dialog.Title>
                </div>
              ) : (
                <Typography variant="body1" color="grey500">
                  <Dialog.Title asChild>
                    <strong>{title}</strong>
                  </Dialog.Title>
                </Typography>
              )}

              <button className={classNames(styles.button)} onClick={onClose}>
                <VisuallyHidden>{t("global.close")}</VisuallyHidden>
                <Close size={24} aria-hidden />
              </button>
            </div>

            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
